import React, { useState, useContext, useEffect } from 'react'
import { Grid, Radio, FormControlLabel, RadioGroup } from '@material-ui/core'
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js'
import Cards from 'react-credit-cards'
import 'react-credit-cards/es/styles-compiled.css'
import '../CreditCard.scss'
import FlexView from 'react-flexview/lib'
import { EventEmitterService, EventKey } from '../../../services/EventEmitterService'
import { awaitWithLoading, isInFlow } from '../../../services/utils'
import { UserApi } from '../../../api/user.api'
import BillingApi from '../../../api/billing.api'
import insuranceApi from '../../../api/insurance.api'
import ChargesApi from '../../../api/charges.api'
import { BrowserView, MobileView } from 'react-device-detect'
import { useLocation } from 'react-router-dom'
import { BackButton } from '../../Core/Buttons/BackButton'
import { CreditCardLogo } from '../../Core/CreditCardLogo'
import { BottomSheetPolicySummary } from '../../Insurance/BottomSheetPolicySummary'
import NewFillButton from '../../Core/NewFillButton'
import { InsurancePurchaseContextStore, INSURANCE_CONTEXT_ACTIONS } from '../../../context/InsuracePurchaseContext'
import { BuyingInsuranceContextStore } from '../../../context/BuyingInsuranceContext'
import { SessionContextStore, SESSION_ACTIONS } from '../../../context/SessionContext'
import { PaymentMethod } from '../../../models/user-models/CreditCardModal'
import { Action } from '../../../models/Action'
import { SessionService } from '../../../services/SessionService'
import { useForm } from 'react-hook-form'
import { reportGTM } from '../../Core/Utilities/GTagManger'
import { isMobile } from 'react-device-detect'
import { CustomErrorPopup } from '../../PopUps/CustomErrorPopup'

let classNames = require('classnames')
const ccstyle = { base: { '::placeholder': { color: 'rgb(159, 173, 197)' } } }

const CheckoutForm = ({ onPurchase, stripe, elements }) => {
  // const stripe = useStripe();
  // const elements = useElements();
  const sessionContext = useContext(SessionContextStore)
  const offersContext = useContext(BuyingInsuranceContextStore)
  const purchaseContext = useContext(InsurancePurchaseContextStore)
  const location = useLocation()
  const [installments, setInstallments] = React.useState('full')
  const [checkoutType, setCheckoutType] = React.useState('Card')
  const [issuer, setIssuer] = useState('')
  const [expire, setExpire] = useState('0000')
  const [last4, setLast4] = useState('')
  const [focusedCC, setFocusedCC] = useState('number')
  const [error, setError] = useState('')
  const [editMode, setEditMode] = useState(!sessionContext.user.hasCreditCard)
  const [enableRecharge, setEnableRecharge] = useState(false)
  const { getValues, register, triggerValidation, errors } = useForm({ mode: 'onChange' })
  const [popUpErrorState, setPopUpErrorState] = React.useState({
    title: '',
    body: '',
    showPopUp: false,
  })
  React.useEffect(() => {
    setEditMode(!sessionContext.user.hasCreditCard)
  }, [sessionContext.user])

  const onCreditCardNumberChange = e => {
    setIssuer(e.brand)
    if (e.complete) {
      const element = elements.getElement(CardExpiryElement)
      element.focus()
      setFocusedCC('expiry')
    }
  }

  const onExpiryChange = e => {
    if (e.complete) {
      const element = elements.getElement(CardCvcElement)
      element.focus()
      setFocusedCC('cvc')
    }
  }

  const onCvcChange = async e => {
    if (e.complete) {
      const MS_IN_DAY = 1000 * 60 * 60 * 24
      const number = elements.getElement(CardNumberElement)
      const res = await stripe.createToken(number)
      const expDate = new Date(res.token.card.exp_year, res.token.card.exp_month, 1)
      const now = new Date()
      const numberOfDaysThisExpired = Math.round((expDate - now) / MS_IN_DAY)
      const data = numberOfDaysThisExpired > 90 ? false : true
      sessionContext.dispatch({ type: SESSION_ACTIONS.SET_CREDIT_CARD_ALERT, data: data })
    }
  }

  const onContinue = () => {
    if (isInFlow(location.pathname)) EventEmitterService.dispatch(EventKey.FLOW_CC_FINISH)
  }

  const onRechargeUser = async () => {
    let res = await awaitWithLoading(ChargesApi.rechargeUser(sessionContext.user.userId))
    let successfullyRecharged
    if (res.ok) {
      setEnableRecharge(false)
      successfullyRecharged = true
    } else {
      EventEmitterService.dispatch(EventKey.ShowError, res)
      successfullyRecharged = false
    }

    reportGTM('', '', { event: 'recharge-user', successfully_recharged: successfullyRecharged })
  }

  const onSave = async () => {
    if (!stripe || !elements) {
      return
    }

    setFocusedCC('number')

    const number = elements.getElement(CardNumberElement)

    EventEmitterService.dispatch(EventKey.ShowPaymentLoader)
    let res = await stripe.createToken(number)

    if (res.error) {
      EventEmitterService.dispatch(EventKey.DismissPaymentLoader)
      setError(res.error.message)
      return false
    } else if (res.token) {
      setError('')
      let ccServerRes = await UserApi.updateCreditCard(res.token)

      EventEmitterService.dispatch(EventKey.DismissPaymentLoader)

      if (ccServerRes.ok) {
        updateCreditCardVisual(res.token.card.exp_month, res.token.card.exp_year, res.token.card.last4, res.token.card.brand)
        setEnableRecharge(false)
        reportGTM('', '', { event: 'successful-payment-saved', 'payment-method': 'cc' })

        return true
      } else {
        setPopUpErrorState({
          title: ccServerRes.data.title,
          body: ccServerRes.data.messages.length > 0 ? ccServerRes.data.messages[0] : '',
          showPopUp: true,
        })
        reportGTM('', '', { event: 'cc-failure', 'payment-method': 'cc' })

        return false
      }
    } else {
      EventEmitterService.dispatch(EventKey.DismissPaymentLoader)
      setError('Unknown error from credit card service provider')
      return false
    }
  }

  const setEmptyCardVisual = () => {
    setExpire('')
    setLast4('')
    setIssuer('')
  }

  const updateCreditCardVisual = (month, year, last4, brand) => {
    setExpire(`${month < 10 ? '0' : ''}${month}/${year}`)
    setLast4(`************${last4}`)
    setIssuer(brand)
  }

  React.useEffect(() => {
    if (!sessionContext.user.hasPaymentMethod) return

    UserApi.getCreditCardInfo().then(res => {
      if (res.ok) {
        updateCreditCardVisual(res.parsedData.expMonth, res.parsedData.expYear, res.parsedData.lastFourDigits, res.parsedData.brand)
        setEditMode(false)
      } else {
        setEmptyCardVisual()
      }
    })
  }, [])

  React.useEffect(() => {
    if (!sessionContext.user.userId || sessionContext.user.userId <= 0) return

    BillingApi.getLastPaymentDetails(sessionContext.user.userId).then(res => {
      if (res.ok) {
        setEnableRecharge(!res.data.is_charged)
      }
    })
  }, [sessionContext.user])

  const editCreditCard = () => {
    setEditMode(true)
  }

  const onEditCreditCard = async () => {
    if (issuer && !editMode) {
      onPurchase()
    } else {
      let val = await awaitWithLoading(onSave())
      if (val) {
        setEditMode(false)
        onPurchase()
        // isInFlow(location.pathname) && onContinue()
        // EventEmitterService.dispatch(EventKey.ShowSuccess, { title: 'Credit Card Added', body: 'Credit Card saved successfully'})
      }
    }
  }

  useEffect(() => {
    if (offersContext.shouldPassCreditCard) {
      onEditCreditCard()
      offersContext.passCreditCard(false)
    }
  }, [offersContext.shouldPassCreditCard])

  const paymentMethodChange = event => {
    setEditMode(event.target.value != PaymentMethod.BANK_TRANSFER)
    purchaseContext.dispatch(new Action(INSURANCE_CONTEXT_ACTIONS.SET_PAYMENT_METHOD, event.target.value))
  }

  return (
    <React.Fragment>
      {SessionService.isAdmin() && isInFlow(location.pathname) && (
        <RadioGroup onChange={paymentMethodChange} value={purchaseContext.paymentMethod}>
          <FlexView>
            <FormControlLabel value={PaymentMethod.STRIPE} label={'Credit Card'} control={<Radio color="primary" />} />
            <FormControlLabel
              value={PaymentMethod.BANK_TRANSFER}
              label={'Generate policy without Credit Card'}
              control={<Radio color="primary" />}
            />
          </FlexView>
        </RadioGroup>
      )}
      {/* <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                <div className='clickable' onClick={() => setInstallments('full')}>
                    <SkyPaper isHighlighted={installments == 'full'}>
                        <FlexView vAlignContent={'center'}>
                            <img className='x-large-icon' src={`${process.env.PUBLIC_URL}/assets/cc_full.jpg`} />
                            <div className='margin-left-basic'>
                                <div className='blue bold text-title'>PAY BASE RATE IN FULL</div>
                                <BlueGrayBox style={{padding: 16, borderRadius: 10, backgroundColor: '#e2e8ff'}} className='margin-top-basic'>
                                    <div className='text-small blue'>Pay <span className='bold'>$300</span> today +monthly per</div>
                                    <div className='text-small margin-top-small blue'>mile charge (estimated 50$ a month)</div>
                                </BlueGrayBox>
                            </div>
                        </FlexView>
                    </SkyPaper>
                </div>
                </Grid>
                <Grid item xs={12} md={6}>
                    <div className='clickable' onClick={() => setInstallments('installments')}>
                        <SkyPaper isHighlighted={installments == 'installments'}>
                            <FlexView vAlignContent={'center'}>
                                <img className='x-large-icon' src={`${process.env.PUBLIC_URL}/assets/cc_installments.jpg`} />
                                <div className='margin-left-basic'>
                                    <div className='blue bold text-title'>PAY BY INSTALLMENTS</div>
                                    <BlueGrayBox style={{padding: 16, borderRadius: 10, backgroundColor: '#e2e8ff'}} className='margin-top-basic'>
                                        <div className='text-small blue'>Pay <span className='bold'>$100</span> today +11 monthly</div>
                                        <div className='text-small margin-top-small blue'>payments (estimated 60$ a month)</div>
                                    </BlueGrayBox>
                                </div>
                            </FlexView>
                        </SkyPaper>
                    </div>
                </Grid>
            </Grid> */}
      <Grid className="margin-top-basic margin-bottom-basic" container spacing={4}>
        <Grid spacing={3} container item xs sm md={10}>
          {purchaseContext.paymentMethod == PaymentMethod.STRIPE && (
            <>
              {editMode && (
                <React.Fragment>
                  <Grid item alignItems="center" xs={12} sm={6}>
                    <span className="bold text-small grey">Card number</span>
                    <CardNumberElement
                      onFocus={() => setFocusedCC('number')}
                      onReady={e => e.focus()}
                      options={{
                        placeholder: 'xxxx-xxxx-xxxx-xxxx',
                        style: ccstyle,
                        classes: { base: 'test' },
                        showIcon: true,
                        iconStyle: 'default',
                      }}
                      onChange={onCreditCardNumberChange}
                    />
                  </Grid>
                  <Grid item alignItems="center" xs={12} sm={3}>
                    <span className="bold text-small grey">Expiration date</span>
                    <CardExpiryElement
                      onFocus={() => setFocusedCC('expiry')}
                      onChange={onExpiryChange}
                      options={{ classes: { base: 'test' }, style: ccstyle }}
                    />
                  </Grid>
                  <Grid item alignItems="center" xs={12} sm={3}>
                    <span className="bold text-small grey">CVC</span>
                    <CardCvcElement
                      onFocus={() => setFocusedCC('cvc')}
                      onChange={onCvcChange}
                      options={{ placeholder: 'xxx', classes: { base: 'cvc-container' }, style: ccstyle }}
                    />
                  </Grid>
                </React.Fragment>
              )}

              {!editMode && (
                <React.Fragment>
                  <FlexView vAlignContent={'center'}>
                    <CreditCardLogo brand={issuer} />
                    <div className="text-small black margin-left-6">xxxx-xxxx-xxxx-{last4.split('*')[last4.split('*').length - 1]}</div>
                    <div onClick={editCreditCard} className="text-small blue clickable underline margin-left-6">
                      Edit card
                    </div>
                  </FlexView>
                </React.Fragment>
              )}
            </>
          )}
          <div className="margin-top-basic">
            <BrowserView>
              {!isInFlow(location.pathname) && editMode && (
                <Grid container>
                  <Grid item xs={6}>
                    <NewFillButton disabled={!editMode} onClick={onEditCreditCard}>
                      Save & Charge
                    </NewFillButton>
                  </Grid>
                </Grid>
              )}
            </BrowserView>
          </div>
          {error && <div className="text-body red margin-top-basic">{error}</div>}
          {SessionService.isAdmin() && enableRecharge && (
            <Grid item xs={6}>
              <NewFillButton onClick={onRechargeUser}>Recharge User</NewFillButton>
            </Grid>
          )}
        </Grid>

        {/* <Grid item xs={12} sm={4}>
                    <Cards
                        preview={true}
                        number={"" + last4}
                        name={" "} // {sessionContext.user.firstName + " " + sessionContext.user.lastName}
                        expiry={expire}
                        cvc={'***'}
                        focused={focusedCC}
                        issuer={issuer} />
                </Grid> */}
      </Grid>
      {/*<BrowserView>
                {isInFlow(location.pathname) && <>
                    <br/>
                    <FlexView className={classNames('space-between full-width margin-top-basic', {'credit-card-buttons-container': !editMode, 'credit-card-buttons-container-edit': editMode})}>
                        <BackButton />
                </FlexView>
                </>}
                </BrowserView>*/}

      <MobileView>
        {isInFlow(location.pathname) && (
          <BottomSheetPolicySummary
            title={'Purchase'}
            onClick={() => {
              onEditCreditCard()
            }}
          />
        )}
        {!isInFlow(location.pathname) && editMode && (
          <FlexView className="margin-top-basic" hAlignContent={'center'}>
            <NewFillButton disabled={!editMode} onClick={onEditCreditCard}>
              Save Changes
            </NewFillButton>
          </FlexView>
        )}
      </MobileView>
      <CustomErrorPopup
        title={popUpErrorState.title}
        body={popUpErrorState.body}
        open={popUpErrorState.showPopUp}
        onCancel={() => setPopUpErrorState({ title: '', body: '', showPopUp: false })}
      />
    </React.Fragment>
  )
}

export default CheckoutForm
